import { Controller } from "@hotwired/stimulus"

/**
 * Window Controller
 * 
 * Handles individual window behavior: drag, resize, minimize, maximize, close.
 * Uses native PointerEvents API for cross-device support.
 * 
 * Targets:
 * - header: Draggable area
 * - content: Window body with Turbo Frame
 * - resizer: Resize handles (multiple)
 * - title: Window title text
 * - loading: Loading indicator
 * - maximizeIcon: Icon that changes based on state
 * 
 * Values:
 * - windowId: Unique identifier for this window
 * - resizable: Whether window can be resized
 * 
 * Events dispatched:
 * - window:focus - When window gains focus
 * - window:close - When window is closed
 * - window:minimize - When window is minimized
 * - window:maximize - When window is maximized/restored
 * - window:titleChange - When the window title changes
 */
export default class extends Controller {
  static targets = ["header", "content", "resizer", "title", "loading", "maximizeIcon", "iframe", "navBack", "navForward"]
  static values = {
    windowId: String,
    resizable: { type: Boolean, default: true },
    baseUrl: { type: String, default: "/admin" }
  }

  // State
  isDragging = false
  isResizing = false
  isMinimized = false
  isMaximized = false
  dragOffset = { x: 0, y: 0 }
  resizeDirection = null
  initialRect = null
  initialPointer = null
  activeResizeHandle = null
  
  // Store pre-maximize dimensions (for restore and state persistence)
  preMaximizeRect = null
  preMaximizeState = null
  contentObserver = null

  // Navigation history
  navHistory = []
  navIndex = -1
  isNavigatingHistory = false

  connect() {
    this.bindEventHandlers()
    this.setupDragging()
    this.setupResizing()
    
    if (this.isIframeMode) {
      // Iframe-based windows (Caja module) — full DOM isolation
      this.setupIframeListeners()
    } else {
      // Turbo Frame-based windows (Admin module)
      this.setupFrameListeners()
      this.element.addEventListener("turbo:frame-load", this.boundHandleFrameLoad)
      this.element.addEventListener("turbo:before-fetch-request", this.boundHandleBeforeFetch)
      this.element.addEventListener("turbo:fetch-request-error", this.boundHandleFetchError)
      this.element.addEventListener("turbo:before-prefetch", this.boundPreventPrefetch)
      this.element.addEventListener("turbo:before-render", this.boundPreventUrlUpdate)
      this.element.addEventListener("turbo:visit", this.boundPreventUrlUpdate)

      // Seed initial URL into navHistory. turbo:before-fetch-request fires
      // before the Stimulus controller connects, so handleBeforeFetch misses it.
      const frame = this.contentTarget?.querySelector("turbo-frame")
      if (frame) {
        const initialSrc = frame.getAttribute("src")
        if (initialSrc) {
          this.pushNavHistory(initialSrc)
        }
      }
    }
    
    // Dispatch registration event
    this.dispatch("registered", {
      detail: { windowId: this.windowIdValue },
      bubbles: true
    })
  }

  disconnect() {
    if (this.isIframeMode) {
      window.removeEventListener("message", this.boundHandleIframeMessage)
      if (this.hasIframeTarget) {
        this.iframeTarget.removeEventListener("load", this.boundHandleIframeLoad)
      }
    } else {
      this.element.removeEventListener("turbo:frame-load", this.boundHandleFrameLoad)
      this.element.removeEventListener("turbo:before-fetch-request", this.boundHandleBeforeFetch)
      this.element.removeEventListener("turbo:fetch-request-error", this.boundHandleFetchError)
      this.element.removeEventListener("turbo:before-prefetch", this.boundPreventPrefetch)
      this.element.removeEventListener("turbo:before-render", this.boundPreventUrlUpdate)
      this.element.removeEventListener("turbo:visit", this.boundPreventUrlUpdate)
    }

    if (this.hasHeaderTarget) {
      this.headerTarget.removeEventListener("pointerdown", this.boundStartDrag)
      this.headerTarget.removeEventListener("pointermove", this.boundDrag)
      this.headerTarget.removeEventListener("pointerup", this.boundStopDrag)
      this.headerTarget.removeEventListener("pointercancel", this.boundStopDrag)
    }

    if (this.hasResizerTarget) {
      this.resizerTargets.forEach(handle => {
        handle.removeEventListener("pointerdown", this.boundStartResize)
      })
    }

    if (this.activeResizeHandle) {
      this.activeResizeHandle.removeEventListener("pointermove", this.boundResize)
      this.activeResizeHandle.removeEventListener("pointerup", this.boundStopResize)
      this.activeResizeHandle.removeEventListener("pointercancel", this.boundStopResize)
      this.activeResizeHandle = null
    }

    if (this.contentObserver) {
      this.contentObserver.disconnect()
      this.contentObserver = null
    }
  }

  bindEventHandlers() {
    this.boundHandleFrameLoad = this.handleFrameLoad.bind(this)
    this.boundHandleBeforeFetch = this.handleBeforeFetch.bind(this)
    this.boundHandleFetchError = this.handleFetchError.bind(this)
    this.boundPreventPrefetch = this.preventPrefetch.bind(this)
    this.boundPreventUrlUpdate = this.preventUrlUpdate.bind(this)

    // Iframe mode handlers
    this.boundHandleIframeLoad = this.handleIframeLoad.bind(this)
    this.boundHandleIframeMessage = this.handleIframeMessage.bind(this)

    this.boundStartDrag = this.startDrag.bind(this)
    this.boundDrag = this.drag.bind(this)
    this.boundStopDrag = this.stopDrag.bind(this)

    this.boundStartResize = this.startResize.bind(this)
    this.boundResize = this.resize.bind(this)
    this.boundStopResize = this.stopResize.bind(this)
  }

  // ==========================================
  // Drag Implementation
  // ==========================================

  setupDragging() {
    if (!this.hasHeaderTarget) return

    this.headerTarget.addEventListener("pointerdown", this.boundStartDrag)
  }

  startDrag(event) {
    // Only handle left mouse button or touch
    if (event.button !== 0) return
    // Don't drag if clicking on controls or nav buttons
    if (event.target.closest(".window-controls") || event.target.closest(".window-nav")) return
    // Don't drag if maximized
    if (this.isMaximized) return

    this.isDragging = true
    this.headerTarget.setPointerCapture(event.pointerId)

    const rect = this.element.getBoundingClientRect()
    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    }

    this.element.classList.add("is-dragging")

    this.headerTarget.addEventListener("pointermove", this.boundDrag)
    this.headerTarget.addEventListener("pointerup", this.boundStopDrag)
    this.headerTarget.addEventListener("pointercancel", this.boundStopDrag)

    event.preventDefault()
  }

  drag(event) {
    if (!this.isDragging) return

    const container = this.element.parentElement
    const containerRect = container.getBoundingClientRect()
    const windowWidth = this.element.offsetWidth
    const windowHeight = this.element.offsetHeight

    let newX = event.clientX - this.dragOffset.x - containerRect.left
    let newY = event.clientY - this.dragOffset.y - containerRect.top

    // Keep window fully within container bounds
    // Left edge: can't go past 0
    // Right edge: can't go past container width - window width
    // Top edge: can't go past 0
    // Bottom edge: can't go past container height - window height
    newX = Math.max(0, Math.min(containerRect.width - windowWidth, newX))
    newY = Math.max(0, Math.min(containerRect.height - windowHeight, newY))

    this.element.style.left = `${newX}px`
    this.element.style.top = `${newY}px`
  }

  stopDrag(event) {
    if (!this.isDragging) return

    this.isDragging = false
    this.headerTarget.releasePointerCapture(event.pointerId)
    this.element.classList.remove("is-dragging")

    this.headerTarget.removeEventListener("pointermove", this.boundDrag)
    this.headerTarget.removeEventListener("pointerup", this.boundStopDrag)
    this.headerTarget.removeEventListener("pointercancel", this.boundStopDrag)
    
    // Notify window manager to save state
    this.dispatchStateChange()
  }

  // ==========================================
  // Resize Implementation
  // ==========================================

  setupResizing() {
    if (!this.resizableValue || !this.hasResizerTarget) return

    this.resizerTargets.forEach(handle => {
      handle.addEventListener("pointerdown", this.boundStartResize)
    })
  }

  startResize(event) {
    if (event.button !== 0) return
    if (this.isMaximized) return

    this.isResizing = true
    this.resizeDirection = event.target.dataset.direction
    this.activeResizeHandle = event.target

    event.target.setPointerCapture(event.pointerId)

    this.initialRect = {
      width: this.element.offsetWidth,
      height: this.element.offsetHeight,
      left: this.element.offsetLeft,
      top: this.element.offsetTop
    }
    this.initialPointer = {
      x: event.clientX,
      y: event.clientY
    }

    this.element.classList.add("is-resizing")

    event.target.addEventListener("pointermove", this.boundResize)
    event.target.addEventListener("pointerup", this.boundStopResize)
    event.target.addEventListener("pointercancel", this.boundStopResize)

    event.preventDefault()
  }

  resize(event) {
    if (!this.isResizing) return

    const container = this.element.parentElement
    const containerRect = container.getBoundingClientRect()
    
    const deltaX = event.clientX - this.initialPointer.x
    const deltaY = event.clientY - this.initialPointer.y
    const dir = this.resizeDirection

    const minWidth = 300
    const minHeight = 200

    let newWidth = this.initialRect.width
    let newHeight = this.initialRect.height
    let newLeft = this.initialRect.left
    let newTop = this.initialRect.top

    // Handle horizontal resize
    if (dir.includes("e")) {
      // Limit to container right edge
      const maxWidth = containerRect.width - this.initialRect.left
      newWidth = Math.max(minWidth, Math.min(maxWidth, this.initialRect.width + deltaX))
    }
    if (dir.includes("w")) {
      // deltaX is negative when moving left
      // Can't go past left edge (0), so newLeft must be >= 0
      // newLeft = initialLeft + deltaX, so deltaX >= -initialLeft
      const minDeltaX = -this.initialRect.left
      const clampedDeltaX = Math.max(minDeltaX, deltaX)
      const clampedWidth = this.initialRect.width - clampedDeltaX
      
      if (clampedWidth >= minWidth) {
        newWidth = clampedWidth
        newLeft = this.initialRect.left + clampedDeltaX
      }
    }

    // Handle vertical resize
    if (dir.includes("s")) {
      // Limit to container bottom edge
      const maxHeight = containerRect.height - this.initialRect.top
      newHeight = Math.max(minHeight, Math.min(maxHeight, this.initialRect.height + deltaY))
    }
    if (dir.includes("n")) {
      // deltaY is negative when moving up
      // Can't go past top edge (0), so newTop must be >= 0
      // newTop = initialTop + deltaY, so deltaY >= -initialTop
      const minDeltaY = -this.initialRect.top
      const clampedDeltaY = Math.max(minDeltaY, deltaY)
      const clampedHeight = this.initialRect.height - clampedDeltaY
      
      if (clampedHeight >= minHeight) {
        newHeight = clampedHeight
        newTop = this.initialRect.top + clampedDeltaY
      }
    }

    this.element.style.width = `${newWidth}px`
    this.element.style.height = `${newHeight}px`
    this.element.style.left = `${newLeft}px`
    this.element.style.top = `${newTop}px`
  }

  stopResize(event) {
    if (!this.isResizing) return

    this.isResizing = false
    this.activeResizeHandle?.releasePointerCapture(event.pointerId)
    this.element.classList.remove("is-resizing")

    if (this.activeResizeHandle) {
      this.activeResizeHandle.removeEventListener("pointermove", this.boundResize)
      this.activeResizeHandle.removeEventListener("pointerup", this.boundStopResize)
      this.activeResizeHandle.removeEventListener("pointercancel", this.boundStopResize)
      this.activeResizeHandle = null
    }
    
    // Notify window manager to save state
    this.dispatchStateChange()
  }

  // ==========================================
  // Window Actions
  // ==========================================

  focus() {
    this.dispatch("focus", {
      detail: { windowId: this.windowIdValue },
      bubbles: true
    })
  }

  minimize() {
    this.isMinimized = true
    this.element.classList.add("is-minimized")

    this.dispatch("minimize", {
      detail: { 
        windowId: this.windowIdValue,
        title: this.hasTitle ? this.titleTarget.textContent : "Ventana"
      },
      bubbles: true
    })
  }

  restore() {
    this.isMinimized = false
    this.element.classList.remove("is-minimized")
    this.focus()

    this.dispatch("restore", {
      detail: { windowId: this.windowIdValue },
      bubbles: true
    })
  }

  toggleMaximize() {
    if (this.isMaximized) {
      this.unmaximize()
    } else {
      this.maximize()
    }
  }

  maximize() {
    // Store current dimensions as CSS values for restore
    this.preMaximizeRect = {
      width: this.element.style.width,
      height: this.element.style.height,
      left: this.element.style.left,
      top: this.element.style.top
    }
    
    // Store as numbers for state persistence
    this.preMaximizeState = {
      x: this.element.offsetLeft,
      y: this.element.offsetTop,
      width: this.element.offsetWidth,
      height: this.element.offsetHeight
    }

    this.isMaximized = true
    this.element.classList.add("is-maximized")

    this.dispatch("maximize", {
      detail: { windowId: this.windowIdValue },
      bubbles: true
    })
    
    // Notify window manager to save state
    this.dispatchStateChange()
  }

  unmaximize() {
    this.isMaximized = false
    this.element.classList.remove("is-maximized")

    // Restore previous dimensions
    if (this.preMaximizeRect) {
      this.element.style.width = this.preMaximizeRect.width
      this.element.style.height = this.preMaximizeRect.height
      this.element.style.left = this.preMaximizeRect.left
      this.element.style.top = this.preMaximizeRect.top
    }
    
    // Clear the preMaximizeState since we're no longer maximized
    this.preMaximizeState = null

    this.dispatch("unmaximize", {
      detail: { windowId: this.windowIdValue },
      bubbles: true
    })
    
    // Notify window manager to save state
    this.dispatchStateChange()
  }

  close() {
    const windowId = this.windowIdValue
    
    // First dispatch close event so manager can unregister
    const event = new CustomEvent("window:close", {
      detail: { windowId },
      bubbles: true,
      cancelable: true
    })
    this.element.dispatchEvent(event)

    // Remove from DOM after animation
    this.element.classList.add("is-closing")
    
    // Use a slightly longer delay to ensure event is processed
    setTimeout(() => {
      this.element.remove()
    }, 200)
  }

  // ==========================================
  // Frame Handling
  // ==========================================

  setupFrameListeners() {
    // Watch for title changes in loaded content
    this.contentObserver = new MutationObserver(this.updateTitleFromContent.bind(this))
    if (this.hasContentTarget) {
      this.contentObserver.observe(this.contentTarget, { childList: true, subtree: true })
    }
  }

  handleFrameLoad(event) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("is-hidden")
    }
    this.updateTitleFromContent()
    
    // Restore URL to /admin (SPA-like behavior)
    this.preventUrlUpdate()
    
    // Notify window manager about URL change for state persistence.
    // Navigation history is captured by handleBeforeFetch and connect(),
    // NOT here — the outer frame src may be stale after inner-frame ops.
    const frame = this.contentTarget?.querySelector("turbo-frame")
    if (frame) {
      const currentSrc = frame.getAttribute("src")
      if (currentSrc) {
        this.dispatch("srcChange", {
          detail: { windowId: this.windowIdValue, src: currentSrc },
          bubbles: true
        })
      }
    }
  }

  // ==========================================
  // Navigation History
  // ==========================================

  pushNavHistory(url) {
    if (this.isNavigatingHistory) {
      this.isNavigatingHistory = false
      this.updateNavButtons()
      return
    }

    // Avoid duplicating current entry
    if (this.navHistory[this.navIndex] === url) {
      this.updateNavButtons()
      return
    }

    // Trim forward history when navigating to a new page
    this.navHistory = this.navHistory.slice(0, this.navIndex + 1)
    this.navHistory.push(url)
    this.navIndex = this.navHistory.length - 1
    this.updateNavButtons()
  }

  goBack() {
    if (this.navIndex <= 0) return
    this.navIndex--
    this.isNavigatingHistory = true
    this.navigateToHistoryEntry()
  }

  goForward() {
    if (this.navIndex >= this.navHistory.length - 1) return
    this.navIndex++
    this.isNavigatingHistory = true
    this.navigateToHistoryEntry()
  }

  navigateToHistoryEntry() {
    const frame = this.contentTarget?.querySelector("turbo-frame")
    if (frame) {
      frame.src = this.navHistory[this.navIndex]
    }
    this.updateNavButtons()
  }

  updateNavButtons() {
    if (this.hasNavBackTarget) {
      this.navBackTarget.disabled = this.navIndex <= 0
    }
    if (this.hasNavForwardTarget) {
      this.navForwardTarget.disabled = this.navIndex >= this.navHistory.length - 1
    }
  }

  handleBeforeFetch(event) {
    // Only show loading for actual frame navigation, not prefetch
    const fetchOptions = event.detail?.fetchOptions
    const headers = fetchOptions?.headers
    const secPurpose =
      typeof headers?.get === "function"
        ? headers.get("X-Sec-Purpose")
        : (headers?.["X-Sec-Purpose"] || headers?.["x-sec-purpose"])
    const isPrefetch = secPurpose === "prefetch"
    
    if (!isPrefetch && this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("is-hidden")
    }

    // Capture ALL GET navigation URLs for navHistory.
    // This is the single capture point — handleFrameLoad does NOT push to navHistory
    // because search/filter/sort forms trigger turbo_stream responses that don't fire
    // turbo:frame-load, and the Turbo-Frame header is always the outer window frame.
    if (!isPrefetch) {
      const method = (fetchOptions?.method || "GET").toUpperCase()
      if (method === "GET") {
        const url = event.detail?.url
        if (url) {
          this.pushNavHistory(url.href || String(url))
        }
      }
    }
  }

  handleFetchError(event) {
    // Hide loading on error
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("is-hidden")
    }
  }

  preventPrefetch(event) {
    // Prevent prefetch inside windows to avoid UI glitches
    event.preventDefault()
  }

  preventUrlUpdate(event) {
    // Restore URL to base URL after any navigation inside windows
    // This keeps the SPA-like behavior where URL always shows /admin or /caja
    const baseUrl = this.baseUrlValue
    if (window.location.pathname !== baseUrl) {
      window.history.replaceState({}, "", baseUrl)
    }
  }

  updateTitleFromContent() {
    // Look for a title element in the loaded content
    const contentTitle = this.contentTarget?.querySelector("[data-window-title]")
    if (contentTitle && this.hasTitleTarget) {
      const newTitle = contentTitle.textContent || contentTitle.dataset.windowTitle
      if (newTitle && newTitle !== this.titleTarget.textContent) {
        this.titleTarget.textContent = newTitle
        this.dispatch("titleChange", {
          detail: { windowId: this.windowIdValue, title: newTitle },
          bubbles: true
        })
      }
    }
  }

  // ==========================================
  // Iframe Handling (Caja module)
  // ==========================================

  setupIframeListeners() {
    if (this.hasIframeTarget) {
      this.iframeTarget.addEventListener("load", this.boundHandleIframeLoad)
    }
    // Listen for postMessage from iframe children (title changes, etc.)
    window.addEventListener("message", this.boundHandleIframeMessage)
  }

  handleIframeLoad() {
    // Hide loading indicator
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("is-hidden")
    }
    
    // Extract title from iframe content (same-origin)
    this.updateTitleFromIframe()
    
    // Notify window manager about URL change for state persistence
    this.notifyIframeSrc()
  }

  handleIframeMessage(event) {
    // Only handle messages from our iframe
    if (!this.hasIframeTarget || event.source !== this.iframeTarget.contentWindow) return
    
    const data = event.data
    if (!data || typeof data !== "object") return
    
    if (data.type === "iframe:titleChange" && data.title && this.hasTitleTarget) {
      this.titleTarget.textContent = data.title
      this.dispatch("titleChange", {
        detail: { windowId: this.windowIdValue, title: data.title },
        bubbles: true
      })
    }
  }

  updateTitleFromIframe() {
    if (!this.hasIframeTarget || !this.hasTitleTarget) return
    
    try {
      const iframeDoc = this.iframeTarget.contentDocument
      if (!iframeDoc) return
      
      // Check for data-window-title attribute first, then fall back to document title
      const titleEl = iframeDoc.querySelector("[data-window-title]")
      const newTitle = titleEl
        ? (titleEl.textContent || titleEl.dataset.windowTitle)
        : iframeDoc.title
      
      if (newTitle && newTitle !== this.titleTarget.textContent) {
        this.titleTarget.textContent = newTitle
        this.dispatch("titleChange", {
          detail: { windowId: this.windowIdValue, title: newTitle },
          bubbles: true
        })
      }
    } catch (e) {
      // Cross-origin iframe — rely on postMessage from iframe_context_controller
    }
  }

  notifyIframeSrc() {
    if (!this.hasIframeTarget) return
    
    try {
      const href = this.iframeTarget.contentWindow.location.href
      const url = new URL(href)
      url.searchParams.delete("_iframe")
      const cleanSrc = url.pathname + url.search
      this.dispatch("srcChange", {
        detail: { windowId: this.windowIdValue, src: cleanSrc },
        bubbles: true
      })
    } catch (e) {
      // Cross-origin fallback: use src attribute
      const src = this.iframeTarget.src
      if (src) {
        const url = new URL(src)
        url.searchParams.delete("_iframe")
        this.dispatch("srcChange", {
          detail: { windowId: this.windowIdValue, src: url.pathname + url.search },
          bubbles: true
        })
      }
    }
  }

  // ==========================================
  // Getters
  // ==========================================

  get isIframeMode() {
    return this.hasIframeTarget
  }

  get hasTitle() {
    return this.hasTitleTarget && this.titleTarget.textContent
  }

  get currentTitle() {
    return this.hasTitleTarget ? this.titleTarget.textContent : "Ventana"
  }

  get bounds() {
    return {
      x: this.element.offsetLeft,
      y: this.element.offsetTop,
      width: this.element.offsetWidth,
      height: this.element.offsetHeight
    }
  }

  // ==========================================
  // State Change Notification
  // ==========================================

  /**
   * Dispatches event to notify window manager that state changed
   * (position, size, maximized state, etc.)
   */
  dispatchStateChange() {
    this.dispatch("stateChange", {
      detail: { windowId: this.windowIdValue },
      bubbles: true
    })
  }
}
